import { Box, Button, Checkbox, Container, FormControlLabel, Paper, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoader } from 'src/contexts/LoaderContext';
import enrollmentAPI from 'src/libs/api/jooicez-backend/enrollment';

export default function PatientEnrollmentPage() {
  const { encryptedId } = useParams<{ encryptedId: string }>();
  const { hideLoader } = useLoader();
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    marketingConsent: false,
    messagingConsent: false,
  });
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    hideLoader();
  }, [hideLoader]);

  useEffect(() => {
    if (!encryptedId) return;
    let mounted = true;
    setIsLoadingPatient(true);
    enrollmentAPI
      .findOne(encryptedId)
      .then((context) => {
        if (!mounted) return;
        setOrganizationName(context.organizationName);
        const patient = context.patient;
        if (!patient) return;
        setValues((current) => ({
          ...current,
          firstName: patient.firstName,
          lastName: patient.lastName,
          phoneNumber: patient.phoneNumber,
          marketingConsent: patient.marketingConsent,
          messagingConsent: patient.messagingConsent,
        }));
      })
      .catch(() => {
        if (mounted) toast.error('Unable to load enrollment details.');
      })
      .finally(() => {
        if (mounted) setIsLoadingPatient(false);
      });
    return () => {
      mounted = false;
    };
  }, [encryptedId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!encryptedId) return;
    setIsSubmitting(true);
    try {
      await enrollmentAPI.upsert(encryptedId, values);
      toast.success('Your details have been saved.');
    } catch {
      toast.error('Unable to save your details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100dvh',
        maxHeight: '100dvh',
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        overflowY: 'auto',
        py: { xs: 2, sm: 6 },
      }}
    >
      <Paper variant="outlined" sx={{ width: '100%', my: 'auto', p: { xs: 2.5, sm: 5 }, borderRadius: 3 }}>
        <Stack spacing={{ xs: 3, sm: 4 }}>
          <Box>
            <Typography component="h1" variant="h3" fontWeight={800}>
              Stay in touch with
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
              {organizationName ?? 'Your care team'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
              Leave your details and we&apos;ll take care of the rest: from appointment reminders to exclusive offers.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={values.firstName}
                onChange={(event) => setValues((current) => ({ ...current, firstName: event.target.value }))}
                disabled={isLoadingPatient || isSubmitting}
                fullWidth
                required
              />
              <TextField
                label="Family name"
                name="lastName"
                autoComplete="family-name"
                value={values.lastName}
                onChange={(event) => setValues((current) => ({ ...current, lastName: event.target.value }))}
                disabled={isLoadingPatient || isSubmitting}
                fullWidth
                required
              />
              <TextField
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                autoComplete="tel"
                value={values.phoneNumber}
                onChange={(event) => setValues((current) => ({ ...current, phoneNumber: event.target.value }))}
                disabled={isLoadingPatient || isSubmitting}
                fullWidth
                required
              />

              <FormControlLabel
                sx={{ alignItems: 'flex-start', m: 0, gap: 0.5 }}
                control={
                  <Checkbox
                    name="marketingConsent"
                    checked={values.marketingConsent}
                    onChange={(event) =>
                      setValues((current) => ({ ...current, marketingConsent: event.target.checked }))
                    }
                    disabled={isLoadingPatient || isSubmitting}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    By checking this box and submitting this form, you consent to receive recurring marketing and
                    promotional text messages from {organizationName ?? '[Med Spa Name]'} at the mobile number provided.
                    Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to opt out.
                    Read our full [Terms of Service] and [Privacy Policy].
                  </Typography>
                }
              />

              <FormControlLabel
                sx={{ alignItems: 'flex-start', m: 0, gap: 0.5 }}
                control={
                  <Checkbox
                    name="messagingConsent"
                    checked={values.messagingConsent}
                    onChange={(event) =>
                      setValues((current) => ({ ...current, messagingConsent: event.target.checked }))
                    }
                    disabled={isLoadingPatient || isSubmitting}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    By checking this box and submitting this form, you consent to receive recurring customer care text
                    messages from {organizationName ?? '[Med Spa Name]'} at the mobile number provided. Message
                    frequency varies. Message and data rates may apply. Reply HELP for help or STOP to opt out. Read our
                    full [Terms of Service] and [Privacy Policy].
                  </Typography>
                }
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!encryptedId || isLoadingPatient || isSubmitting}
              >
                {isSubmitting ? 'Sending…' : 'Send'}
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="center" spacing={1}>
            <Button variant="text" size="small">
              Privacy policy
            </Button>
            <Button variant="text" size="small">
              Term of service
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
